/**
 * Revenue: what a trip is worth, what has been collected, and the totals.
 *
 * Pure functions only — no DOM, no Firebase, injected clock. Every number goes
 * through `lib/money.js`; this file adds no second parser and no second copy
 * of the arithmetic.
 *
 * ── What the legacy actually computes ─────────────────────────────────────
 * Verified against `MBM Luxury Prototype/hosting/app.html` on 15 Aug 2026.
 *
 *   5158–5163   the Financial Summary page: nine cost rows
 *               (`finCat{i}` / `fin{i}amt` / `fin{i}note`), the readonly
 *               `finTotal`, insurance, and three payment rows
 *               (`pay{i}_amt` / `_due` / `_paid` / `_method`)
 *   6949–6962   `calcFinTotal()` — sums the cost column into `finTotal`
 *   7041–7096   `renderFinancialDashboard()` — trip total, payments, progress
 *   7219–7237   `getTripValue()` / `getTripPaidAmount()`
 *   8630–8698   `renderRevenueDashboard()` — all-time, this-year, per-month
 *   9627–9660   `buildFullSheetRow()` — the CSV / Zapier export
 *
 * ── THREE READINGS OF "REVENUE", AND THEY DISAGREE ────────────────────────
 * This is the open question from HANDOFF §9, not a bug with an obvious fix,
 * so this file NAMES the three rather than quietly picking one:
 *
 *   line items   sum of `fin{i}amt` — the Account screen (`/v2/account`) and
 *                legacy `getTripValue` (app.html:7219)
 *   quoted       `finTotal` alone — `/insights` (`rev()`), and the CSV export
 *                (app.html:9630)
 *   best         line items, falling back to `finTotal` when they are empty —
 *                legacy's own trip dashboard (app.html:7049–7051)
 *
 * For the trip named "Disney", whose `finTotal` is the string
 * `"$10,000 - $20,000"` and whose line items are empty, those are $0, $10,000
 * and $10,000. `revenueSource()` says which a trip has, so a screen can show
 * the disagreement instead of inheriting it. The default is `line-items`,
 * which is what `/v2/account` reports today: changing what an advisor's
 * revenue card says is a product decision, not a port.
 *
 * ── Bugs found, each with a named regression test ─────────────────────────
 *
 * 1. THE EXPORT'S COMMISSION COLUMNS ARE ALWAYS EMPTY.
 *    `buildFullSheetRow` (app.html:9631) reads `d.finCommission || d.commTotal`.
 *    Neither field exists anywhere in the 15,903 lines of `app.html` — that
 *    line is the only occurrence of either name. The real field is
 *    `commGrandTotal`. So every CSV an advisor has ever downloaded, and every
 *    row ever pushed to their Google Sheet, has a blank Commission and a blank
 *    Commission Split. `/insights`'s `comm(t)` reads the same two phantom
 *    fields, which is why its Commission stat is $0 for every trip.
 *    `commission.js#tripCommission` is the reading that works.
 *
 * 2. THE EXPORT RE-INTRODUCES THE $1,000,020,000 PARSER.
 *    app.html:9630 — `parseFloat((d.finTotal||'').replace(/[^0-9.]/g,''))`.
 *    That is the digit-strip HANDOFF §9 blames for the billion-dollar
 *    dashboard, still live in the CSV path. `"$10,000 - $20,000"` exports as
 *    1000020000.
 *
 * 3. `formatMoney` PRINTS A REFUND AS INCOME.
 *    app.html:7036 — `'$' + Math.abs(n).toLocaleString(...)`. A credit of
 *    -$500 renders as "$500". Display lives in `lib/format.js`; this file
 *    keeps refunds negative so nothing downstream has to guess.
 *
 * 4. THE REVENUE DASHBOARD'S "BOOKED" IS A FOURTH DEFINITION.
 *    app.html:8641 — `TRIPS.filter(t => t.status === 'booked')`, while
 *    `/insights` counts `confirmed` or `completed` (insights.html:478) and the
 *    Account screen uses `money.js#isBooked`. `revenueTotals` uses `isBooked`,
 *    the one definition with a test.
 */

import { FIN_SLOTS, amount, clampCents, fromCents, isBooked, isCancelled, paymentRows, summaryNumber, toCents, tripOutstanding, tripPaid, tripScheduled, tripValue } from './money.js'

/** How revenue may be read off a trip. See the header. */
export const REVENUE_MODES = Object.freeze(['line-items', 'quoted', 'best'])

/**
 * The nine cost rows, with their labels — what the advisor typed, in order.
 * @param {Record<string, any>|null|undefined} trip
 * @param {number} [slots]
 * @returns {Array<{slot: number, label: string, amount: number, note: string}>}
 */
export function costLines(trip, slots = FIN_SLOTS) {
  const data = (trip && trip.data) || {}
  const count = Number.isFinite(slots) ? Math.max(0, Math.floor(Number(slots))) : 0
  const rows = []
  for (let i = 0; i < count; i++) {
    const value = amount(data[`fin${i}amt`])
    const label = String(data[`finCat${i}`] ?? '').trim()
    if (value === 0 && !label) continue
    rows.push({ slot: i, label: label || `Item ${i + 1}`, amount: value, note: String(data[`fin${i}note`] ?? '') })
  }
  return rows
}

/**
 * The quoted total the advisor typed or `calcFinTotal` wrote into `finTotal`.
 * @param {Record<string, any>|null|undefined} trip
 * @returns {number}
 */
export function quotedTotal(trip) {
  const pre = summaryNumber(trip, 'quoted')
  if (pre !== null) return pre
  return amount((trip && trip.data && trip.data.finTotal) || '')
}

/**
 * Which reading of revenue this trip actually supports.
 *
 * `'both'` is the case worth surfacing: the line items and the quoted total
 * both exist and may not match, and no screen tells anyone.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @returns {'line-items'|'quoted'|'both'|'none'}
 */
export function revenueSource(trip) {
  const lines = tripValue(trip) !== 0
  const quoted = quotedTotal(trip) !== 0
  if (lines && quoted) return 'both'
  if (lines) return 'line-items'
  if (quoted) return 'quoted'
  return 'none'
}

/**
 * What a trip is worth, under a named reading. Default matches `/v2/account`.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @param {{mode?: 'line-items'|'quoted'|'best'}} [options]
 * @returns {number}
 */
export function tripRevenue(trip, options = {}) {
  const mode = options.mode || 'line-items'
  if (mode === 'quoted') return quotedTotal(trip)
  const lines = tripValue(trip)
  if (mode === 'best' && lines === 0) return quotedTotal(trip)
  return lines
}

/**
 * How much of the trip the client has paid, 0–100.
 *
 * Legacy caps at 100 (app.html:7095–7096) and shows 0 when the trip has no
 * value; both are kept, because a progress bar wider than its track is a
 * layout bug and a bar drawn against an unknown total is a lie. Overpayment
 * is still visible in `tripOutstanding`, which goes negative.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @param {{mode?: 'line-items'|'quoted'|'best'}} [options]
 * @returns {number}
 */
export function paymentProgress(trip, options = {}) {
  const total = tripRevenue(trip, { mode: options.mode || 'best' })
  if (total <= 0) return 0
  const pct = Math.round((tripPaid(trip) / total) * 100)
  if (!Number.isFinite(pct)) return 0
  return Math.min(100, Math.max(0, pct))
}

/**
 * Everything the trip's Financial Dashboard shows, as numbers.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @param {{mode?: 'line-items'|'quoted'|'best'}} [options]
 * @returns {{revenue: number, source: string, collected: number, scheduled: number, outstanding: number, unbilled: number, progress: number, payments: ReturnType<typeof paymentRows>}}
 */
export function tripFinancials(trip, options = {}) {
  const mode = options.mode || 'best'
  const revenue = tripRevenue(trip, { mode })
  const collected = tripPaid(trip)
  const scheduled = tripScheduled(trip)
  return {
    revenue,
    source: revenueSource(trip),
    collected,
    scheduled,
    outstanding: tripOutstanding(trip),
    /* Trip value the payment schedule does not account for yet. Legacy has no
       equivalent: it shows "Remaining" as the unpaid rows only, so a $5,000
       trip with a single $500 deposit row reads as $500 remaining. */
    unbilled: fromCents(clampCents(toCents(revenue) - toCents(scheduled))),
    progress: paymentProgress(trip, { mode }),
    payments: paymentRows(trip),
  }
}

/**
 * Revenue across many trips.
 *
 * Cancelled trips are excluded (money.js). Archived ones are NOT: legacy's
 * revenue dashboard counts every trip in `TRIPS` including archived
 * (app.html:8637), and an advisor's all-time revenue should not shrink
 * because they tidied their board. `includeCancelled` exists for a caller
 * that genuinely wants the gross.
 *
 * @param {Array<Record<string, any>>|null|undefined} trips
 * @param {{mode?: 'line-items'|'quoted'|'best', includeCancelled?: boolean}} [options]
 * @returns {{trips: number, booked: number, revenue: number, collected: number, scheduled: number, outstanding: number}}
 */
export function revenueTotals(trips, options = {}) {
  const list = Array.isArray(trips) ? trips.filter(Boolean) : []
  const mode = options.mode || 'line-items'
  let revenueCents = 0
  let collectedCents = 0
  let scheduledCents = 0
  let counted = 0
  let booked = 0

  for (const trip of list) {
    if (!options.includeCancelled && isCancelled(trip)) continue
    counted++
    if (isBooked(trip)) booked++
    revenueCents = clampCents(revenueCents + toCents(tripRevenue(trip, { mode })))
    collectedCents = clampCents(collectedCents + toCents(tripPaid(trip)))
    scheduledCents = clampCents(scheduledCents + toCents(tripScheduled(trip)))
  }

  return {
    trips: counted,
    booked,
    revenue: fromCents(revenueCents),
    collected: fromCents(collectedCents),
    scheduled: fromCents(scheduledCents),
    outstanding: fromCents(clampCents(scheduledCents - collectedCents)),
  }
}

/**
 * Revenue per calendar month, oldest first — the Revenue dashboard's bar chart.
 *
 * The clock is injected. Legacy builds its twelve buckets with
 * `new Date(thisYear, now.getMonth() - i, 1)` (app.html:8662), which is fine,
 * and then matches trips with `getMonth()`/`getFullYear()` on a `new Date(t.created)`
 * — local time. A trip created at 23:30 UTC on the 31st lands in the previous
 * month for anyone west of UTC, which is the same off-by-one-day class of bug
 * §5r found on the concierge calendar. `bucketOf` is injectable for exactly
 * that reason; the default reads UTC, so the bucket does not depend on where
 * the advisor is sitting.
 *
 * @param {Array<Record<string, any>>|null|undefined} trips
 * @param {{nowMs?: number, months?: number, mode?: 'line-items'|'quoted'|'best', createdMs?: (trip: any) => number|null}} [options]
 * @returns {Array<{key: string, year: number, month: number, revenue: number, trips: number}>}
 */
export function revenueByMonth(trips, options = {}) {
  const list = Array.isArray(trips) ? trips.filter(Boolean) : []
  const months = Number.isFinite(options.months) ? Math.max(1, Math.floor(Number(options.months))) : 12
  const nowMs = Number.isFinite(options.nowMs) ? Number(options.nowMs) : 0
  const mode = options.mode || 'line-items'
  const readCreated =
    options.createdMs ||
    ((trip) => {
      const raw = trip && trip.created
      if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
      const parsed = Date.parse(String(raw ?? ''))
      return Number.isFinite(parsed) ? parsed : null
    })

  const now = new Date(nowMs)
  /** @type {Map<string, {key: string, year: number, month: number, cents: number, trips: number}>} */
  const buckets = new Map()
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    const key = d.toISOString().slice(0, 7)
    buckets.set(key, { key, year: d.getUTCFullYear(), month: d.getUTCMonth(), cents: 0, trips: 0 })
  }

  for (const trip of list) {
    if (isCancelled(trip)) continue
    const created = readCreated(trip)
    if (created == null || !Number.isFinite(created)) continue
    const key = new Date(created).toISOString().slice(0, 7)
    const bucket = buckets.get(key)
    if (!bucket) continue
    bucket.cents = clampCents(bucket.cents + toCents(tripRevenue(trip, { mode })))
    bucket.trips++
  }

  return [...buckets.values()].map((b) => ({
    key: b.key,
    year: b.year,
    month: b.month,
    revenue: fromCents(b.cents),
    trips: b.trips,
  }))
}

/**
 * Mean revenue per trip. Zero trips is 0, not NaN — `0/0` on a stat card is
 * the same class of failure as the `$NaN` this whole module exists to stop.
 *
 * @param {Array<Record<string, any>>|null|undefined} trips
 * @param {{mode?: 'line-items'|'quoted'|'best'}} [options]
 * @returns {number}
 */
export function averageTripRevenue(trips, options = {}) {
  const totals = revenueTotals(trips, options)
  if (totals.trips === 0) return 0
  return fromCents(clampCents(toCents(totals.revenue) / totals.trips))
}
